import { size } from '@floating-ui/dom'
import classNames from 'classnames'
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  useRef,
  RefObject,
  ReactNode,
} from 'react'
import { type CascadingMenuOption } from '@opallabs/concepts'
import { Spacing } from '@opallabs/gem'
import {
  MultiSelectDiffObject,
  useDropdownPositioning,
  useMultiSelectDiff,
} from '@opallabs/hooks'
import { Button, ButtonSize } from '../Button'
import * as CascadingMenu from '../CascadingMenu'
import { Icon } from '../Icon'
import { Portal } from '../Portal'
import { Token } from '../Token'
import { useRadixDropdownBlurHelper } from '../utils/RadixDropdownBlurHelper'
import * as styles from './styles'

export type MultiTokenSelectProps<T extends CascadingMenuOption> = {
  initialSelection?: Array<T>
  onChanges: (changes: MultiSelectDiffObject<T>) => void
  options: Array<T>
  placeholder?: string
  emptyMessage?: string
  readOnly?: boolean
  persistentOption?: {
    label: string
    leadingNode?: ReactNode
    onSelect?: () => void
    trailingNode?: ReactNode
  }
}

function setSelectedOptions(
  options: Array<CascadingMenuOption>,
  selectedItems: Array<CascadingMenuOption>
): Array<CascadingMenuOption> {
  return options.map((o) => ({
    ...o,
    selected: !!selectedItems.find((s) => s.label === o.label),
    ...(o.childOptions && {
      childOptions: setSelectedOptions(o.childOptions, selectedItems),
    }),
  }))
}

export function MultiTokenSelect<T extends CascadingMenuOption>({
  initialSelection = [],
  onChanges,
  readOnly,
  options,
  placeholder,
  emptyMessage,
  persistentOption,
}: MultiTokenSelectProps<T>) {
  const [isOpen, setIsOpen] = useState(false)
  const innerRef = useRef<HTMLDivElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)
  // Makes `useDropdownPositioning` fire when `<CascadingMenu.Root>` mounts
  const [menuRef, setMenuRef] = useState<RefObject<HTMLDivElement>>({
    current: null,
  })
  const { onSelect, selectedItems, getMultiSelectDiff } = useMultiSelectDiff({
    initialSelection,
    key: 'value',
  })
  const optionsWithSelected = setSelectedOptions(options, selectedItems)

  useEffect(
    () => {
      onChanges(getMultiSelectDiff())
    },
    // Intentionally leaving out dependencies, otherwise this effect will run way more often than it should.
    // `useMultiSelectDiff` needs to be reworked so that the diff state is derived instead of a getter,
    // the result gets out of sync with the react render cycle very easily
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedItems]
  )

  const onRemoveToken = (option: T) => {
    onSelect(option)
  }

  const handleCloseMenu = () => {
    if (isOpen) {
      setIsOpen(false)
      buttonRef.current?.focus()
    }
  }

  const onSelectToken = (option: T) => {
    onSelect(option)
    handleCloseMenu()
  }

  const handleOpenMenu = (
    e:
      | React.MouseEvent<HTMLButtonElement>
      | React.KeyboardEvent<HTMLButtonElement>
  ) => {
    if ('key' in e && (e.key === 'Enter' || e.key === 'Space')) {
      setIsOpen(true)
    } else {
      setIsOpen(true)
    }
  }

  const handleContainerClick: React.MouseEventHandler<HTMLDivElement> = (e) => {
    if (readOnly) {
      e.preventDefault()
    }
    if (selectedItems.length === 0) {
      buttonRef.current?.click()
    } else {
      buttonRef.current?.focus()
    }
  }

  const handleContainerFocus: React.FocusEventHandler<HTMLDivElement> = (e) => {
    if (readOnly) {
      e.preventDefault()
    }
  }

  const handleMenuRef = useCallback(
    (node: HTMLDivElement | null) => setMenuRef({ current: node }),
    []
  )

  const positioningOptions = useMemo(
    () => ({
      computePosition: {
        middleware: [
          size({
            padding: Spacing.S,
            apply({ availableHeight, elements }) {
              elements.floating.style.maxBlockSize =
                Math.min(400, availableHeight) + 'px'
            },
          }),
        ],
      },
    }),
    []
  )

  const { isPositioned } = useDropdownPositioning(
    innerRef.current,
    menuRef.current,
    positioningOptions
  )

  // `<Portal>` makes `<CascadingMenu.Root>` render outside `innerRef`
  const blurRefs = useMemo(() => [innerRef, menuRef], [menuRef])
  useRadixDropdownBlurHelper(isOpen, blurRefs, handleCloseMenu)

  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    <div
      ref={innerRef}
      className={classNames(styles.selectors.root, {
        [styles.selectors.readOnly]: readOnly,
      })}
      css={styles.mainContainer}
      tabIndex={readOnly ? -1 : 0}
      onClick={handleContainerClick}
      onFocus={handleContainerFocus}
    >
      <div className={styles.selectors.input} css={styles.inputContainer}>
        {selectedItems.length === 0 && (
          <span
            className={styles.selectors.noSelectionText}
            css={styles.noSelectedText}
          >
            None
          </span>
        )}
        {selectedItems.map((selectedItem) => (
          <Token
            css={styles.token}
            key={selectedItem.label}
            value={selectedItem.label}
            text={selectedItem.label}
            leadingNode={selectedItem.leadingNode}
            trailingNode={selectedItem.trailingNode}
            removable={!readOnly}
            onRemoveToken={() => onRemoveToken(selectedItem)}
          />
        ))}
        {!readOnly && (
          <Button
            className={styles.selectors.menuTrigger}
            css={styles.trigger}
            ref={buttonRef}
            variant="secondary"
            name="Add option"
            icon={<Icon glyph="gem-add-small" />}
            size={ButtonSize.Small}
            onClick={handleOpenMenu}
          />
        )}
      </div>
      {isOpen && (
        <Portal>
          <CascadingMenu.Root
            ref={handleMenuRef}
            className={classNames({
              [styles.stateSelectors.isPositioned]: isPositioned,
            })}
            css={styles.menu}
            interactiveFooter={Boolean(persistentOption?.onSelect)}
            items={optionsWithSelected}
            onCloseMenu={handleCloseMenu}
            onSelect={onSelectToken as (o: CascadingMenuOption) => void}
          >
            <CascadingMenu.Search
              includeNestedOptions={true}
              placeholder={placeholder}
            />
            <CascadingMenu.SearchableItems
              emptyMessage={emptyMessage}
              multiselectable={true}
            />
            {Boolean(persistentOption && persistentOption.onSelect) && (
              <CascadingMenu.InteractiveFooter
                onSelect={persistentOption?.onSelect}
              >
                {persistentOption?.leadingNode}
                <span>{persistentOption?.label}</span>
                {persistentOption?.trailingNode}
              </CascadingMenu.InteractiveFooter>
            )}
            {Boolean(persistentOption && !persistentOption.onSelect) && (
              <CascadingMenu.InertFooter>
                {persistentOption?.leadingNode}
                <span>{persistentOption?.label}</span>
                {persistentOption?.trailingNode}
              </CascadingMenu.InertFooter>
            )}
          </CascadingMenu.Root>
        </Portal>
      )}
    </div>
  )
}
MultiTokenSelect.displayName = 'Lapidary: MultiTokenSelect'
